import React, { Component } from 'react';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import StarRatings from 'react-star-ratings';
import ReactHtmlParser from 'react-html-parser';
import InviteColleagues from '../../common/InviteColleagues';
import TagDisplay from '../../TagDisplay';
import { postRequest, deleteRequest } from '../../../requestUtils';
import { analyticsTrack } from '../../../analyticsUtils';
import userProfile from '../../../../assets/images/icons/user-profile-new.png';
import locationIcon from '../../../../assets/images/icons/location.png';
import './ConsultantSearchCard.scss';

const renderField = (attribute, icon, answer) => (
  <span className={`${attribute} body-text-2 mr-16`}>
    <i className={`fa ${icon} mr-5`} />
    {answer}
  </span>
);

const renderStarRatings = (rating, note, recModal) => (
  <div className="rating-avg">
    <div className="g-row--always lowered-margin">
      <StarRatings
        rating={rating}
        starRatedColor="#FED500"
        starHoverColor="#FED500"
        starDimension="20px"
        starSpacing="2px"
        numberOfStars={5}
        name="rating_avg"
      />
      <span className={recModal ? 'num-ratings' : 'body-text-2 medium-weight'}>
        {note}
      </span>
    </div>
  </div>
);

const renderRequestProposal = (
  consultant,
  query,
  location,
  proposalRequested,
  projectResourceId,
) => {
  const classes = `btn btn-${location === 'search' ? 'secondary' : `primary action-button ${proposalRequested ? 'disabled' : ''}`} get-connected ${location}-get-connected`;
  const proposalFromProject = location === 'search' ? '' : `&project_id=${projectResourceId}`;
  return (
    <a
      href={`/users/${consultant.resource_id}/request_proposal?query_id=${query}${proposalFromProject}`}
      target="_blank"
      className={classes}
      onClick={() => {
        analyticsTrack('Click Request Proposal on Profile Card', { location: location === 'search' ? 'Search Index' : location });
      }}
      type="button"
      rel="noreferrer"
    >
      Request Proposal
    </a>
  );
};

const renderBioSnippets = (consultant, location) => (
  <div>
    {/* This is necessary because consultant.bio contains html elements
        returned from algolia to highlight search matches. Using ReactHtmlParser
        because it is safer than dangerouslySetInnerHTML
    */}
    {ReactHtmlParser(`<div class="bio body-text-2 text-prewrap">${consultant.bio}</div>`)}

    {location === 'search'
      && (
      <div className="snippets mb-2">
        {consultant.snippets.map((s) => (
          ReactHtmlParser(`<p>...${s}...</p>`)
        ))}
      </div>
      )}
  </div>
);

class ConsultantSearchCard extends Component {
  constructor(props) {
    super(props);
    const { consultant } = this.props;
    this.state = {
      favoriteId: consultant.user_favorite_id,
      colleagues: consultant.uninvited_colleagues || [],
      invitedColleagues: consultant.invited_colleagues || [],
      proposalRequested: props.proposalRequested || false,
    };

    this.toggleSave = this.toggleSave.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.renderInviteColleagues = this.renderInviteColleagues.bind(this);
    this.requestProposal = this.requestProposal.bind(this);
    this.renderProfileHeader = this.renderProfileHeader.bind(this);
  }

  toggleSave() {
    const { consultant, csrfToken } = this.props;
    const { favoriteId } = this.state;
    if (favoriteId) {
      deleteRequest(
        `/user_favorites/${favoriteId}`,
        csrfToken,
      ).then(() => { this.setState({ favoriteId: null }); });
    } else {
      postRequest(
        '/user_favorites',
        {
          user_favorite: {
            favorited_id: consultant.id,
            favorited_type: 'ConsultantProfile',
          },
        },
        csrfToken,
      ).then((response) => {
        this.setState({ favoriteId: response.data });
        analyticsTrack('Click Save on the Profile Card', { location: 'Search Index' });
      });
    }
  }

  requestProposal() {
    const { csrfToken, consultant, projectResourceId } = this.props;
    const toastMessage = `You requested a proposal from ${consultant.name} Clora will be in touch shortly with next steps.`;
    postRequest(
      `/projects/${projectResourceId}/project_invitations`,
      { consultant_resource_id: consultant.resource_id, request_proposal: true },
      csrfToken,
    ).then(() => {
      analyticsTrack('Clicked on Request Proposal', {
        location: 'Project Show',
        resource_id: consultant.resource_id,
      });
    });
    toast.success(
      <span>
        {toastMessage}
      </span>,
    );
    this.setState({ proposalRequested: true });
  }

  handleChange(invited, newColleagues) {
    const { invitedColleagues } = this.state;
    this.setState({
      colleagues: newColleagues,
      invitedColleagues: invitedColleagues.concat(invited),
    });
  }

  renderInviteColleagues() {
    const {
      csrfToken,
      inviteUrl,
      consultant,
      inviterEmailDomain,
      location,
    } = this.props;
    const { colleagues, invitedColleagues } = this.state;
    const buttonType = location !== 'search' ? 'btn-secondary' : 'btn-secondary-no-border';
    return (
      <InviteColleagues
        disabled={false}
        location={location}
        invitedColleagues={invitedColleagues}
        uninvitedColleagues={colleagues}
        csrfToken={csrfToken}
        resource="Profile"
        resourceId={consultant.resource_id}
        inviteUrl={inviteUrl}
        handleFeedbacks={this.handleChange}
        styleType="button"
        styleClass={`${buttonType} action-button`}
        cta={(
          <div>
            <i className="fa fa-share-alt" />
            <span className="ps-3">Share</span>
          </div>
)}
        header="Share with colleagues"
        subtext="Send this profile to other team members
        and make a decision about this candidate."
        ownerMessagePlaceholder="How does this candidate fit your needs?..."
        limitInvitesWithinCompany
        inviterEmailDomain={inviterEmailDomain}
        preventCloseModalAfterSubmit
        confirmationScreenAfterSend
      />
    );
  }

  renderProfileHeader() {
    const { consultant } = this.props;
    return (
      <div className="name-review-share-save">
        <div className="g-row--always">
          <div className="avatar-image">
            <img
              src={userProfile}
              className="user-profile"
              alt="User-Profile"
            />
          </div>

          <div>
            <h2
              id="consultant-name"
              className="consultant-name fs-hide"
            >
              {consultant.name_initials}
            </h2>

            {renderStarRatings(consultant.rating_avg || 0, `(${consultant.num_ratings})`, true)}

            {consultant.city_location
              && (
              <div>
                <img
                  src={locationIcon}
                  className="location-icon"
                  alt="Location-Marker"
                />
                <span className="location-text">
                  {consultant.city_location}
                </span>
              </div>
              )}
          </div>
        </div>
      </div>
    );
  }

  render() {
    const {
      consultant,
      location,
      query,
      viewingAsEmployer,
      projectSeniority,
      projectNonPriorityTags,
      projectPriorityTags,
      projectResourceId,
    } = this.props;
    const { favoriteId, proposalRequested } = this.state;

    return (
      <div
        id="consultant-search-card"
        className="consultant-search-card"
      >
        {location === 'Recommendations Modal'
          && (
          <a
            id="consultant-profile"
            className="consultant-profile"
            href={`/users/${consultant.resource_id}?project=${projectResourceId}`}
            target="_blank"
            onClick={() => {
              analyticsTrack('Click View Profile on Profile Card', { location });
            }}
            rel="noreferrer"
          >
            {this.renderProfileHeader()}
          </a>
          )}

        {location === 'Project Show Consultant Recommendations'
          && (
          <a
            id="consultant-profile"
            className="consultant-profile"
            href={`/users/${consultant.resource_id}?project=${projectResourceId}`}
            onClick={() => {
              analyticsTrack('Click View Profile on Profile Card', { location });
            }}
          >
            {this.renderProfileHeader()}
          </a>
          )}

        <div className="consultant-search-card-non-profile">
          {location === 'search'
          && (
          <div>
            <div className="name-review-share-save-search mb-5">
              <div className="g-row--always">
                <h2 className="mb-2 fs-hide">
                  <a className="consultant-name" href={`users/${consultant.resource_id}`} target="_blank" rel="noreferrer">
                    {consultant.name}
                  </a>
                </h2>

                {consultant.rating_avg
                      && renderStarRatings(
                        consultant.rating_avg,
                        consultant.rating_avg.toFixed(1),
                        false,
                      )}
              </div>

              <div className="g-row--always">
                {this.renderInviteColleagues()}
                <button
                  className="btn btn-secondary-no-border"
                  onClick={this.toggleSave}
                  type="button"
                  disabled={!viewingAsEmployer}
                >
                  <i className={`${favoriteId ? 'fas' : 'far'} fa-heart`} />
                  <span className="ps-3">Save</span>
                </button>
              </div>
            </div>

            <TagDisplay
              consultant={consultant}
              areRoles
            />

            <div className="level-yrs-location">
              {consultant.years_of_experience && renderField(
                'years-of-experience',
                'fa-id-card-alt',
                `${consultant.years_of_experience} year${consultant.years_of_experience === 0 || consultant.years_of_experience > 1 ? 's' : ''} of experience`,
              )}
              {consultant.seniority_levels && consultant.seniority_levels.length > 0 && renderField('seniority_levels', 'fa-certificate', consultant.seniority_levels.join(', '))}
              {consultant.location && renderField('location', 'fa-map-marker-alt', consultant.location)}
            </div>

            {renderBioSnippets(consultant, location)}
          </div>
          )}

          <TagDisplay
            consultant={consultant}
            location={location}
            seniorityTags={projectSeniority}
            priorityTags={projectPriorityTags}
            nonPriorityTags={projectNonPriorityTags}
          />

          {location === 'Recommendations Modal'
              && renderBioSnippets(consultant, location)}

          {location !== 'search'
          && (
          <div>
            <TagDisplay
              consultant={consultant}
              areRoles
            />

            <div
              id="card-actions"
              className="consultant-search-card-actions container"
            >
              <div
                id="action-container"
                className="button-action-container"
              >
                <a
                  role="button"
                  type="button"
                  className={`btn btn-primary action-button ${proposalRequested && 'disabled'} get-connected`}
                  onClick={this.requestProposal}
                  tabIndex={0}
                >
                  Request Proposal
                </a>
              </div>
              <div className="button-action-container">
                {this.renderInviteColleagues()}
              </div>
            </div>
          </div>
          )}

          {location === 'search'
          && (
          <div className="consultant-search-card-actions">
            <a
              href={`/users/${consultant.resource_id}?query=${query}`}
              target="_blank"
              className={`btn btn-secondary-no-border view-profile ${location}-view-profile`}
              onClick={() => {
                analyticsTrack('Click View Profile on Profile Card', { location: 'Search Index' });
              }}
              rel="noreferrer"
            >
              <i className="fa fa-expand" />
              View Profile
            </a>
            {renderRequestProposal(
              consultant,
              query,
              location,
              proposalRequested,
              projectResourceId,
            )}
          </div>
          )}
        </div>
        {location !== 'search' && proposalRequested
        && (
        <div className="proposal-requested">
          <span>Proposal Requested</span>
        </div>
        )}
      </div>
    );
  }
}

ConsultantSearchCard.propTypes = {
  consultant: PropTypes.shape({}).isRequired,
  location: PropTypes.string.isRequired,
  csrfToken: PropTypes.string.isRequired,
  query: PropTypes.number,
  inviteUrl: PropTypes.string.isRequired,
  inviterEmailDomain: PropTypes.string,
  viewingAsEmployer: PropTypes.bool,
  projectSeniority: PropTypes.arrayOf(PropTypes.string),
  projectNonPriorityTags: PropTypes.arrayOf(PropTypes.string),
  projectPriorityTags: PropTypes.arrayOf(PropTypes.string),
  projectResourceId: PropTypes.string,
  proposalRequested: PropTypes.bool,

};

ConsultantSearchCard.defaultProps = {
  query: null,
  inviterEmailDomain: '',
  viewingAsEmployer: true,
  projectSeniority: [],
  projectNonPriorityTags: [],
  projectPriorityTags: [],
  projectResourceId: '',
  proposalRequested: false,

};

export default ConsultantSearchCard;
